import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpParams} from "@angular/common/http";
import {map} from "rxjs/operators";
import {Observable, tap} from "rxjs";
import {MvsCrudService} from "features/core/shared/service/crud/mvs-crud.service";
import {CoreApiResponseDto} from "features/core/shared/dto/core-api-response.dto";
import {ProvisionRunKpiResponseDto} from "../../model/dto/api/provision-run-kpi.dto";
import {ProvisionRunActivationCheckDto} from "../../model/dto/api/provision-run-activation-check.dto";
import {ProvisionRunActivateRequestDto} from "../../model/dto/api/provision-run-activate-request.dto";
import {ProvisionRunListFigureDto} from "../../model/dto/api/provision-run-list-figure.dto";
import {ProvisionRunAgentSummaryDto, ProvisionRunAgentSummaryKind} from "../../model/dto/api/provision-run-agent-summary.dto";
import {ProvisionClarificationResponseDto, ProvisionClarificationStatusFilter} from "../../model/dto/api/provision-clarification-row.dto";
import {ProvisionManualInterventionResponseDto} from "../../model/dto/api/provision-manual-intervention-row.dto";
import {CpAgentStatementResult} from "../../interface/cp-agent-statement-result.interface";
import {SUPPRESS_ERROR_HANDLER} from "features/core/shared/service/crud/suppress-error-token";
import {cpDownloadBlobWithServerName$} from "../domain/cp-file-download";

/**
 * Custom API service for cp.ProvisionRun action endpoints (03-architektur-backend.md §11.1;
 * bm BillingRunService mirror, PAT §B/§J). All action endpoints are async on the backend
 * (`@Async` + `DtoApiResponse`) and return immediately — the run-detail step bar (04 §5.1)
 * polls via the Spring Batch job indicator (`JbSpringBatchIndicatorService`, jobNamePattern
 * 'cp%') and re-reads the run on the running -> not-running transition, never on the POST
 * response itself. All actions require the um right `cp_runExecution` (U-08).
 */
@Injectable({
    providedIn: 'root'
})
export class CpProvisionRunService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionRuns');
    }

    /**
     * POST /{id}/calculate — starts (or restarts) the calculation job. When contractIds is
     * given, only that subset of contracts is (re-)calculated (03 §11.1).
     */
    public calculate(provisionRunId: number, contractIds?: number[]): Observable<CoreApiResponseDto> {
        let query = '';
        if (contractIds?.length) {
            query = '?contractIds=' + contractIds.join(',');
        }
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/calculate${query}`, {});
    }

    /**
     * POST /{id}/activate — starts the activation job (IN_REVIEW/ACTIVATION_FAILED -> IN_ACTIVATION -> ACTIVATED).
     * The body (contract C-2) carries the override reason and the confirmations; it stays optional so
     * a call without it fails on the status rule instead of on deserialization.
     */
    public activate(provisionRunId: number, body?: ProvisionRunActivateRequestDto): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/activate`, body ?? {});
    }

    /** GET /{id}/activationCheck — read-only pre-check of the activation (contract C-1). */
    public getActivationCheck(provisionRunId: number): Observable<ProvisionRunActivationCheckDto> {
        return this.http.get<ProvisionRunActivationCheckDto>(this.apiUrl + `/${provisionRunId}/activationCheck`);
    }

    /** GET /listFigures?runIds=1,2,3 — sign-correct sum, error count and who/when per run (contract C-3, max. 100 ids). */
    public getListFigures(runIds: number[]): Observable<ProvisionRunListFigureDto[]> {
        const params = new HttpParams().set('runIds', runIds.join(','));
        return this.http.get<ProvisionRunListFigureDto[]>(this.apiUrl + '/listFigures', {params});
    }

    /** GET /{id}/agentSummary?kind=contract|agent — sign-correct per-agent totals of a run (contract C-4). */
    public getAgentSummary(provisionRunId: number, kind: ProvisionRunAgentSummaryKind): Observable<ProvisionRunAgentSummaryDto> {
        const params = new HttpParams().set('kind', kind);
        return this.http.get<ProvisionRunAgentSummaryDto>(this.apiUrl + `/${provisionRunId}/agentSummary`, {params});
    }

    /** GET /clarifications — contracts not settled / data errors for sales (contract C-5). */
    public getClarifications(provisionTypeId?: number, status?: ProvisionClarificationStatusFilter,
                             runId?: number): Observable<ProvisionClarificationResponseDto> {
        let params = new HttpParams();
        if (provisionTypeId != null) {
            params = params.set('provisionTypeId', provisionTypeId);
        }
        if (status) {
            params = params.set('status', status);
        }
        if (runId != null) {
            params = params.set('runId', runId);
        }
        return this.http.get<ProvisionClarificationResponseDto>(this.apiUrl + '/clarifications', {params});
    }

    /** GET /manualInterventions — manual bookings, transfers, overwrites, agent changes of a period (contract C-6). */
    public getManualInterventions(from?: string, to?: string,
                                  provisionTypeId?: number): Observable<ProvisionManualInterventionResponseDto> {
        let params = new HttpParams();
        if (from) {
            params = params.set('from', from);
        }
        if (to) {
            params = params.set('to', to);
        }
        if (provisionTypeId != null) {
            params = params.set('provisionTypeId', provisionTypeId);
        }
        return this.http.get<ProvisionManualInterventionResponseDto>(this.apiUrl + '/manualInterventions', {params});
    }

    /** POST /{id}/payout — starts the payout job (guards per 03 §8: payout brake, valuta monotonicity). */
    public payout(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/payout`, {});
    }

    /** POST /{id}/review — manual transition CALCULATED -> IN_REVIEW. */
    public review(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/review`, {});
    }

    /** POST /{id}/complete — manual transition PAID_OUT -> COMPLETED. */
    public complete(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/complete`, {});
    }

    /**
     * POST /{id}/abort — manual transition -> ABORTED (allowed up to and including
     * IN_REVIEW/CALCULATION_FAILED); calculated-only bookings of the run are discarded.
     */
    public abort(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/abort`, {});
    }

    /** POST /{id}/updateBalances — manual trigger of the balance job (bm updateBalances mirror). */
    public updateBalances(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/updateBalances`, {});
    }

    /**
     * GET /{id}/accountingExport?format=csv — the accounting hand-over of a paid-out run AS A FILE
     * (03-architektur-backend.md §8.4 step 4, BR-080, original requirement Z.261 "Meldung an die
     * Buchhaltung").
     *
     * <p>The endpoint is AUTHENTICATED like every other call of this frontend: the http interceptor
     * adds `IgAuthorization` and `x-alpha-tenant` to an `HttpClient` request, and a plain
     * `<a href>` would carry neither — the browser would navigate to a 401/403 page instead of
     * saving a file. The answer is therefore fetched as a BLOB through the same `HttpClient` and
     * the save is triggered from it.
     *
     * <p>Build shape taken 1:1 from the repo original
     * `features/feature-bm/bm/service/api/billing-run-sepa-direct-debit-document.service.ts`
     * (`downloadCsv$` / `downloadFile$`): `responseType: 'blob'` + `tap` that creates an object
     * URL, clicks a generated anchor carrying the `download` attribute and revokes the URL again.
     * The Observable is returned so the CALLER owns the busy state and the error handling — the
     * step bar spins its button on it and demotes on a 403.
     *
     * @param provisionRunId id of the run whose hand-over is downloaded
     * @param fileName       name the file is saved under (see the step bar's file-name builder)
     */
    public downloadAccountingExportCsv(provisionRunId: number, fileName: string): Observable<Blob> {
        const params = new HttpParams().set('format', 'csv');
        return this.downloadFile$(this.apiUrl + `/${provisionRunId}/accountingExport`, params, fileName);
    }

    /**
     * Fetches an authenticated file answer as a blob and hands it to the browser to save.
     *
     * <p>Mirror of `BillingRunSepaDirectDebitDocumentService#downloadFile$` — same anchor mechanics,
     * same revoke, so the two modules download a file in exactly one way.
     */
    private downloadFile$(url: string, params: HttpParams, fileName: string): Observable<Blob> {
        return this.http.get(url, {params, responseType: 'blob'}).pipe(
            tap((blob: Blob) => {
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName;
                link.click();
                window.URL.revokeObjectURL(downloadUrl);
            })
        );
    }

    /**
     * GET /{id}/agentStatements/{provisionAgentId}/pdf — the statement of one agent as a PDF: a
     * DRAFT (watermarked, not persisted) while the run is calculated/in review/activated, the
     * stored FINAL one afterwards. The server answers 404/409 with a German message, which the
     * caller shows. File name from `Content-Disposition`, the fallback below otherwise.
     */
    public downloadAgentStatementPdf$(provisionRunId: number, provisionAgentId: number): Observable<Blob> {
        return cpDownloadBlobWithServerName$(this.http, 'GET',
            this.apiUrl + `/${provisionRunId}/agentStatements/${provisionAgentId}/pdf`, new HttpParams(),
            `Provisionsabrechnung-${provisionRunId}-${provisionAgentId}.pdf`, true).pipe(map(response => response.body as Blob));
    }

    /**
     * POST /{id}/agentStatements/{provisionAgentId}/create — issues the single FINAL statement
     * (idempotent: an existing ACTIVE one is returned as `SKIPPED_EXISTING`). A refusal (400/403/404/409)
     * carries the German text in `error.data` — see `cpServerMessage`; the global error toast is
     * suppressed, the caller reports.
     */
    public createAgentStatement$(provisionRunId: number, provisionAgentId: number): Observable<CpAgentStatementResult> {
        return this.http.post<CpAgentStatementResult>(
            this.apiUrl + `/${provisionRunId}/agentStatements/${provisionAgentId}/create`, {},
            {context: new HttpContext().set(SUPPRESS_ERROR_HANDLER, true)});
    }

    /** POST /{id}/agentStatements/create — starts the job that issues the final statements of all agents of the run. */
    public createAgentStatements$(provisionRunId: number): Observable<CoreApiResponseDto> {
        return this.http.post<CoreApiResponseDto>(this.apiUrl + `/${provisionRunId}/agentStatements/create`, {},
            {context: new HttpContext().set(SUPPRESS_ERROR_HANDLER, true)});
    }

    /**
     * GET /kpis — dashboard start-page KPIs grouped by ProvisionType (01 §6 K-01…K-14; bm
     * getBillingKpis mirror). Read-only.
     */
    public getKpis(provisionTypeIds?: number[]): Observable<ProvisionRunKpiResponseDto> {
        let params = new HttpParams();
        if (provisionTypeIds?.length) {
            params = params.set('provisionTypeIds', provisionTypeIds.join(','));
        }
        return this.http.get<ProvisionRunKpiResponseDto>(this.apiUrl + '/kpis', {params});
    }

}
